import { type HttpClient } from '@packages/core/http';

import type {
    DeleteCaseRequest,
    DeleteCaseResponse,
    DeleteCidCvnCaseRequest,
    DeleteCidCvnCaseResponse,
    DeleteDraftWorksheetSubmissionRequest,
    DeleteDraftWorksheetSubmissionResponse,
    GetAccountsByRetailerRequest,
    GetAccountsByRetailerResponse,
    GetAssetRequest,
    GetAssetResponse,
    GetAssetUploadStatusRequest,
    GetAssetUploadStatusResponse,
    GetCaseAssetsRequest,
    GetCaseAssetsResponse,
    GetCaseDetailsRequest,
    GetCaseDetailsResponse,
    GetCaseSummariesRequest,
    GetCaseSummariesResponse,
    GetCaseTechnicianAssignmentsRequest,
    GetCaseTechnicianAssignmentsResponse,
    GetCommentsRequest,
    GetCommentsResponse,
    GetCvtDiagnosticDetailsRequest,
    GetCvtDiagnosticDetailsResponse,
    GetDtcCodesRequest,
    GetDtcCodesResponse,
    GetFailCodeConcernsRequest,
    GetFailCodeConcernsResponse,
    GetFailCodeFiguresRequest,
    GetFailCodeFiguresResponse,
    GetFailCodeFrequenciesRequest,
    GetFailCodeFrequenciesResponse,
    GetFailCodeLocationsResponse,
    GetFailCodeSearchRequest,
    GetFailCodeSearchResponse,
    GetFailCodeSectionsByFailCodeRequest,
    GetFailCodeSectionsByFailCodeResponse,
    GetFailCodeSectionsRequest,
    GetFailCodeSectionsResponse,
    GetFailCodesRequest,
    GetFailCodesResponse,
    GetFailCodeSubsectionsByFailCodeRequest,
    GetFailCodeSubsectionsByFailCodeResponse,
    GetFailCodeSubsectionsRequest,
    GetFailCodeSubsectionsResponse,
    GetPartNumberSearchRequest,
    GetPartNumberSearchResponse,
    GetRetailerProfileOptionsRequest,
    GetRetailerProfileOptionsResponse,
    GetVehicleDetailsRequest,
    GetVehicleDetailsResponse,
    GetVinValidationRequest,
    GetVinValidationResponse,
    GetWiringDiagramImageRequest,
    GetWiringDiagramImageResponse,
    GetWiringDiagramSubsectionsRequest,
    GetWiringDiagramSubsectionsResponse,
    GetWorksheetFormRequest,
    GetWorksheetFormResponse,
    GetWorksheetSubmissionRequest,
    GetWorksheetSubmissionResponse,
    GetWorksheetTitlesRequest,
    GetWorksheetTitlesResponse,
    PatchCaseRequest,
    PatchCaseResponse,
    PatchDtcCodeRequest,
    PatchDtcCodeResponse,
    PatchRetailerProfileRequest,
    PatchRetailerProfileResponse,
    PatchWorksheetSubmissionRequest,
    PatchWorksheetSubmissionResponse,
    PostAnswerAssetsRequest,
    PostAnswerAssetsResponse,
    PostAssignmentCompletionRequest,
    PostAssignmentCompletionResponse,
    PostCaseAssetsRequest,
    PostCaseAssetsResponse,
    PostCaseStatusRequest,
    PostCaseStatusResponse,
    PostCidCvnCaseStatusRequest,
    PostCidCvnCaseStatusResponse,
    PostCommentRequest,
    PostCommentResponse,
    PostCvtFileUploadRequest,
    PostCvtFileUploadResponse,
    PostCvtSendRequest,
    PostCvtSendResponse,
    PostCvtUploadUrlsRequest,
    PostCvtUploadUrlsResponse,
    PostUpdateAssetsRequest,
    PostUpdateAssetsResponse,
} from './dtos';

/**
 * Low-level HTTP wrapper for all sts-backend endpoints. Each method maps 1:1 to a single REST endpoint;
 * no business logic, no data transformation. Domain operations compose these calls via use cases.
 *
 * @param http - the underlying HTTP client used to make requests
 * @example
 * const client = new StsBackendClient(httpClient);
 * const response = await client.getCaseDetails({ caseNumber: 'CASE-123' });
 */
export class StsBackendClient {
    /**
     * Create a new STS backend client.
     *
     * @param http - the underlying HTTP client used to make requests
     */
    constructor(private http: HttpClient) {}

    // ──────────────────────────────────────────────
    // Case Details & Management
    // ──────────────────────────────────────────────

    /**
     * Retrieve full details for a Techline case by case number.
     *
     * @param request - contains the case number to look up
     * @param signal - optional abort signal for cancellation
     * @returns the case details response
     */
    getCaseDetails(request: GetCaseDetailsRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<GetCaseDetailsResponse>({
            method: 'GET',
            url: `/northstar/retrieveCase/${request.caseNumber}/v1`,
            ignoreCache: true,
            signal,
        });
    }

    /**
     * Update (patch) a Techline draft case.
     *
     * @param request - contains the update body with fields to patch
     * @param signal - optional abort signal for cancellation
     * @returns the updated case response
     */
    updateCase(request: PatchCaseRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<PatchCaseResponse>({
            method: 'PATCH',
            url: '/northstar/draftCase/v1',
            body: request.updateBody,
            ignoreCache: true,
            signal,
        });
    }

    /**
     * Delete a Techline draft case by case number.
     *
     * @param request - contains the case number to delete
     * @param signal - optional abort signal for cancellation
     * @returns the deletion response
     */
    deleteCase(request: DeleteCaseRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<DeleteCaseResponse>({
            method: 'DELETE',
            url: `/northstar/deleteDraftCase/${request.caseNumber}/v1`,
            signal,
        });
    }

    /**
     * Update the status of a Techline case (e.g. DRAFT to IN_REVIEW).
     *
     * @param request - contains the status update body
     * @param signal - optional abort signal for cancellation
     * @returns the status update response
     */
    updateCaseStatus(request: PostCaseStatusRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<PostCaseStatusResponse>({
            method: 'POST',
            url: '/northstar/updateCaseStatus/v1',
            body: request.updateBody,
            ignoreCache: true,
            signal,
        });
    }

    // ──────────────────────────────────────────────
    // Cases (Dashboard)
    // ──────────────────────────────────────────────

    /**
     * Retrieve a list of Techline cases.
     *
     * @param request - contains the filter criteria for cases
     * @param signal - optional abort signal for cancellation
     * @returns the list of cases matching the criteria
     */
    getCases(request: GetCaseSummariesRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<GetCaseSummariesResponse>({
            method: 'GET',
            url: '/northstar/retrieve-cases' + this.http.queryString(request),
            ignoreCache: true,
            signal,
        });
    }

    // ──────────────────────────────────────────────
    // Assets
    // ──────────────────────────────────────────────

    /**
     * Retrieve all assets associated with a Techline case.
     *
     * @param request - contains the case number whose assets to retrieve
     * @param signal - optional abort signal for cancellation
     * @returns the case assets response
     */
    retrieveCaseAssets(request: GetCaseAssetsRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<GetCaseAssetsResponse>({
            method: 'GET',
            url: `/northstar/retrieveCaseAssets/${request.caseNumber}/v1`,
            ignoreCache: true,
            signal,
        });
    }

    /**
     * Create new assets on a Techline case by posting asset metadata.
     *
     * @param request - contains the asset creation payload
     * @param signal - optional abort signal for cancellation
     * @returns the created assets response (includes presigned upload URLs)
     */
    postAssets(request: PostCaseAssetsRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<PostCaseAssetsResponse>({
            method: 'POST',
            url: '/northstar/postAssets/v1',
            body: request.assetsBody,
            ignoreCache: true,
            signal,
        });
    }

    /**
     * Update existing assets on a Techline case.
     *
     * @param request - contains the asset update payload
     * @param signal - optional abort signal for cancellation
     * @returns the updated assets response
     */
    updateAssets(request: PostUpdateAssetsRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<PostUpdateAssetsResponse>({
            method: 'POST',
            url: '/northstar/updateAssets',
            body: request.assetsBody,
            ignoreCache: true,
            signal,
        });
    }

    /**
     * Retrieve a single asset by its identifier.
     *
     * @param request - contains the asset ID to retrieve
     * @returns the asset response
     */
    retrieveAsset(request: GetAssetRequest) {
        return this.http.orchestrateRequest<GetAssetResponse>({
            method: 'GET',
            url: `/northstar/retrieveAsset/${request.assetId}/v1`,
            ignoreCache: true,
        });
    }

    /**
     * Check the upload processing status of an asset.
     *
     * @param request - contains the asset ID whose upload status to check
     * @param signal - optional abort signal for cancellation
     * @returns the upload status response
     */
    retrieveAssetUploadStatus(request: GetAssetUploadStatusRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<GetAssetUploadStatusResponse>({
            method: 'GET',
            url: `/northstar/retrieveAssetUploadStatus/${request.assetId}/v1`,
            ignoreCache: true,
            signal,
        });
    }

    // ──────────────────────────────────────────────
    // Comments
    // ──────────────────────────────────────────────

    /**
     * Retrieve all comments on a Techline case.
     *
     * @param request - contains the case number whose comments to retrieve
     * @param signal - optional abort signal for cancellation
     * @returns the comments response
     */
    getComments(request: GetCommentsRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<GetCommentsResponse>({
            method: 'GET',
            url: `/northstar/retrieveTechShareComments/${request.caseNumber}/v1`,
            ignoreCache: true,
            signal,
        });
    }

    /**
     * Add a comment to a Techline case.
     *
     * @param request - contains the comment creation body
     * @param signal - optional abort signal for cancellation
     * @returns the created comment response
     */
    addComment(request: PostCommentRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<PostCommentResponse>({
            method: 'POST',
            url: '/northstar/createTechShareComment/v1',
            body: request.body,
            ignoreCache: true,
            signal,
        });
    }

    // ──────────────────────────────────────────────
    // Assignments
    // ──────────────────────────────────────────────

    /**
     * Retrieve technician assignments for a Techline case.
     *
     * @param request - contains the case number whose assignments to retrieve
     * @param signal - optional abort signal for cancellation
     * @returns the technician assignments response
     */
    getCaseTechnicianAssignments(request: GetCaseTechnicianAssignmentsRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<GetCaseTechnicianAssignmentsResponse>({
            method: 'GET',
            url: `/northstar/case-technician-assignment/${request.caseNumber}`,
            ignoreCache: true,
            signal,
        });
    }

    /**
     * Update the completion status of a technician assignment.
     *
     * @param request - contains the assignment ID, technician name, and completion flag
     * @param signal - optional abort signal for cancellation
     * @returns the update response
     */
    updateAssignmentCompletionStatus(request: PostAssignmentCompletionRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<PostAssignmentCompletionResponse>({
            method: 'POST',
            url: '/northstar/case-technician-assignment',
            body: request,
            ignoreCache: true,
            signal,
        });
    }

    // ──────────────────────────────────────────────
    // Retailer Profiles
    // ──────────────────────────────────────────────

    /**
     * Patch the retailer profile associated with a Techline case.
     *
     * @param request - contains the case number, username, retailer number, and status code
     * @returns the patch response
     */
    patchRetailerProfile(request: PatchRetailerProfileRequest) {
        return this.http.orchestrateRequest<PatchRetailerProfileResponse>({
            method: 'PATCH',
            url: '/northstar/patchCaseRetailerProfile/v1',
            body: request,
            ignoreCache: true,
        });
    }

    /**
     * Retrieve retailer profile options filtered by retailer number or associated with the authenticated user.
     *
     * @param request - contains the optional retailer number filter
     * @param signal - optional abort signal for cancellation
     * @returns the list of retailer profile options
     */
    getRetailerProfiles(request: GetRetailerProfileOptionsRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<GetRetailerProfileOptionsResponse>({
            method: 'GET',
            url: '/northstar/retrieveRetailer/v1' + this.http.queryString(request),
            ignoreCache: true,
            signal,
        });
    }

    // ──────────────────────────────────────────────
    // Worksheet Titles
    // ──────────────────────────────────────────────

    /**
     * Retrieve worksheet titles available for Techline cases, filtered by pilot user status.
     *
     * @param request - contains the pilot user flag
     * @param signal - optional abort signal for cancellation
     * @returns the list of worksheet titles
     */
    getWorksheetTitles(request: GetWorksheetTitlesRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<GetWorksheetTitlesResponse>({
            method: 'GET',
            url: `/techline/worksheet-titles?isPilotUser=${request.isPilotUser}`,
            ignoreCache: true,
            signal,
        });
    }

    // ──────────────────────────────────────────────
    // Fail Codes (Techline endpoints)
    // ──────────────────────────────────────────────

    /**
     * Search fail codes by keyword query for a given vehicle.
     *
     * @param request - contains vehicle identifiers and search query
     * @param signal - optional abort signal for cancellation
     * @returns the matching fail codes
     */
    searchFailCodes(request: GetFailCodeSearchRequest, signal?: AbortSignal) {
        const url = '/techline/fail-codes/search' + this.http.queryString(request);

        return this.http.orchestrateRequest<GetFailCodeSearchResponse>({
            method: 'GET',
            url,
            signal,
        });
    }

    /**
     * Retrieve fail code sections filtered by a specific fail code.
     *
     * @param request - contains vehicle identifiers and fail code to filter by
     * @param signal - optional abort signal for cancellation
     * @returns the matching sections
     */
    fetchPossibleSections(request: GetFailCodeSectionsByFailCodeRequest, signal?: AbortSignal) {
        const queryString = this.http.queryString(request);

        return this.http.orchestrateRequest<GetFailCodeSectionsByFailCodeResponse>({
            method: 'GET',
            url: '/techline/fail-codes/fail-code-sections' + queryString,
            signal,
        });
    }

    /**
     * Retrieve fail code subsections filtered by a specific fail code and section.
     *
     * @param request - contains vehicle identifiers, fail code, and parent section
     * @param signal - optional abort signal for cancellation
     * @returns the matching subsections
     */
    fetchPossibleSubsections(request: GetFailCodeSubsectionsByFailCodeRequest, signal?: AbortSignal) {
        const queryString = this.http.queryString(request);

        return this.http.orchestrateRequest<GetFailCodeSubsectionsByFailCodeResponse>({
            method: 'GET',
            url: '/techline/fail-codes/fail-code-subsections' + queryString,
            signal,
        });
    }

    /**
     * Retrieve all top-level fail code sections for a vehicle.
     *
     * @param request - contains vehicle identifiers
     * @param signal - optional abort signal for cancellation
     * @returns the sections list
     */
    fetchFailCodeSections(request: GetFailCodeSectionsRequest, signal?: AbortSignal) {
        const url = '/techline/fail-codes/sections' + this.http.queryString(request);

        return this.http.orchestrateRequest<GetFailCodeSectionsResponse>({
            method: 'GET',
            url,
            signal,
        });
    }

    /**
     * Look up fail code subsections within a given section.
     *
     * @param request - contains vehicle identifiers and parent section
     * @param signal - optional abort signal for cancellation
     * @returns the subsections list
     */
    lookupFailCodeSubsections(request: GetFailCodeSubsectionsRequest, signal?: AbortSignal) {
        const url = '/techline/fail-codes/subsections' + this.http.queryString(request);

        return this.http.orchestrateRequest<GetFailCodeSubsectionsResponse>({
            method: 'GET',
            url,
            signal,
        });
    }

    /**
     * Look up fail code figures within a given section and subsection.
     *
     * @param request - contains vehicle identifiers, section, and subsection
     * @param signal - optional abort signal for cancellation
     * @returns the figures list
     */
    lookupFailCodeFigures(request: GetFailCodeFiguresRequest, signal?: AbortSignal) {
        const url = '/techline/fail-codes/figures' + this.http.queryString(request);

        return this.http.orchestrateRequest<GetFailCodeFiguresResponse>({
            method: 'GET',
            url,
            signal,
        });
    }

    /**
     * Look up specific fail codes within a given hierarchy path. When `partNumber` is provided,
     * the query uses part-number-based lookup; otherwise it uses the section/subsection/figure path.
     *
     * @param request - contains vehicle identifiers and hierarchy path or part number
     * @param signal - optional abort signal for cancellation
     * @returns the fail codes list
     */
    lookupFailCodes(request: GetFailCodesRequest, signal?: AbortSignal) {
        // Branch query parameters based on whether a part number is provided
        const queryString = request.partNumber
            ? this.http.queryString({
                  figureCode: request.figureCode,
                  modelCode: request.modelCode,
                  modelYear: request.modelYear,
                  optionCode: request.optionCode,
                  partNumber: request.partNumber,
              })
            : this.http.queryString({
                  figureCode: request.figureCode,
                  modelCode: request.modelCode,
                  modelYear: request.modelYear,
                  optionCode: request.optionCode,
                  sectionNumber: request.sectionNumber,
                  subsectionNumber: request.subsectionNumber,
              });

        return this.http.orchestrateRequest<GetFailCodesResponse>({
            method: 'GET',
            url: '/techline/fail-codes' + queryString,
            signal,
        });
    }

    /**
     * Search part numbers by keyword query for a given vehicle.
     *
     * @param request - contains vehicle identifiers and search query
     * @param signal - optional abort signal for cancellation
     * @returns the matching part numbers
     */
    searchPartNumbers(request: GetPartNumberSearchRequest, signal?: AbortSignal) {
        const url = '/techline/fail-codes/part-number/search' + this.http.queryString(request);

        return this.http.orchestrateRequest<GetPartNumberSearchResponse>({
            method: 'GET',
            url,
            signal,
        });
    }

    /**
     * Retrieve wiring diagram subsection figures for a vehicle.
     *
     * @param request - contains vehicle identifiers and optional VIN
     * @param signal - optional abort signal for cancellation
     * @returns the wiring diagram subsections
     */
    fetchFailCodeWiringDiagramSubsections(request: GetWiringDiagramSubsectionsRequest, signal?: AbortSignal) {
        const queryString = this.http.queryString(request);

        return this.http.orchestrateRequest<GetWiringDiagramSubsectionsResponse>({
            method: 'GET',
            url: '/techline/fail-codes/electrical-wiring-diagrams/subsection-figures' + queryString,
            signal,
        });
    }

    /**
     * Fetch a wiring diagram image as a Blob for a given figure code.
     *
     * @param request - contains the figure code identifying the diagram
     * @param signal - optional abort signal for cancellation
     * @returns the diagram image as a Blob
     */
    fetchWiringDiagramImage(request: GetWiringDiagramImageRequest, signal?: AbortSignal) {
        const queryString = this.http.queryString({ figureId: request.figureCode });

        return this.http.orchestrateRequest<GetWiringDiagramImageResponse>({
            method: 'GET',
            url: '/fail-codes/electrical-wiring-diagram' + queryString,
            responseType: 'blob',
            signal,
        });
    }

    // ──────────────────────────────────────────────
    // CID/CVN
    // ──────────────────────────────────────────────

    /**
     * Update the status of a CID-CVN case.
     *
     * @param request - contains the status update body
     * @param signal - optional abort signal for cancellation
     * @returns the status update response
     */
    updateCidCvnCaseStatus(request: PostCidCvnCaseStatusRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<PostCidCvnCaseStatusResponse>({
            method: 'POST',
            url: '/northstar/cid-cvn/updateCaseStatus/v1',
            body: request.updateBody,
            ignoreCache: true,
            signal,
        });
    }

    /**
     * Delete a CID-CVN draft case by case number.
     *
     * @param request - contains the case number to delete
     * @returns the deletion response
     */
    deleteCidCvnCase(request: DeleteCidCvnCaseRequest) {
        return this.http.orchestrateRequest<DeleteCidCvnCaseResponse>({
            method: 'DELETE',
            url: `/northstar/deleteCidCvnDraftCase/${request.caseNumber}/v1`,
        });
    }

    // ──────────────────────────────────────────────
    // Worksheet Operations
    // ──────────────────────────────────────────────

    /**
     * Retrieve a single worksheet submission by case number and submission ID.
     *
     * @param request - contains the case number and submission ID
     * @returns the worksheet submission response
     */
    retrieveWorksheetSubmission(request: GetWorksheetSubmissionRequest) {
        const params = new URLSearchParams({
            casenumber: request.caseNumber,
            submissionId: request.submissionId,
        });

        return this.http.orchestrateRequest<GetWorksheetSubmissionResponse>({
            method: 'GET',
            url: `/techline/retrieveWorksheetSubmission?${params}`,
            ignoreCache: true,
        });
    }

    /**
     * Patch (update) a worksheet submission with answers.
     *
     * @param request - contains the submission ID, case number, answers, and status
     * @returns the patched submission response
     */
    worksheetAnswerSubmission(request: PatchWorksheetSubmissionRequest) {
        return this.http.orchestrateRequest<PatchWorksheetSubmissionResponse>({
            method: 'PATCH',
            url: '/northstar/patchWorksheetSubmission',
            body: request,
        });
    }

    /**
     * Delete a draft worksheet submission by its submission ID.
     *
     * @param request - contains the submission ID to delete
     * @returns the deletion response
     */
    deleteDraftWorksheetSubmission(request: DeleteDraftWorksheetSubmissionRequest) {
        return this.http.orchestrateRequest<DeleteDraftWorksheetSubmissionResponse>({
            method: 'DELETE',
            url: `/northstar/deleteDraftWorksheetSubmission/${request.submissionId}`,
        });
    }

    /**
     * Fetch a worksheet form definition by its identifier.
     *
     * @param request - contains the worksheet form ID
     * @returns the worksheet form definition
     */
    fetchWorksheetFormById(request: GetWorksheetFormRequest) {
        return this.http.orchestrateRequest<GetWorksheetFormResponse>({
            method: 'GET',
            url: `/admin/worksheet/${request.worksheetFormId}`,
        });
    }

    /**
     * Create answer assets (upload file metadata) for worksheet answers.
     *
     * @param request - contains the list of asset items and case status
     * @returns the created assets response with presigned upload URLs
     */
    createAnswerAssets(request: PostAnswerAssetsRequest) {
        return this.http.orchestrateRequest<PostAnswerAssetsResponse>({
            method: 'POST',
            url: '/northstar/postAssets/v1',
            body: request,
        });
    }

    // ──────────────────────────────────────────────
    // Accounts
    // ──────────────────────────────────────────────

    /**
     * Search for user accounts by retailer number and text query.
     *
     * @param request - contains the retailer code and search text
     * @returns the list of matching account records
     */
    getAccountsByRetailerNumber(request: GetAccountsByRetailerRequest) {
        return this.http.orchestrateRequest<GetAccountsByRetailerResponse>({
            method: 'GET',
            url: `/accounts/findUsers${this.http.queryString(request)}`,
            ignoreCache: true,
        });
    }

    // ──────────────────────────────────────────────
    // Fail Codes (QMR endpoints)
    // ──────────────────────────────────────────────

    /**
     * Fetch all available fail code locations.
     *
     * @param signal - optional abort signal for cancellation
     * @returns the fail code locations
     */
    fetchFailCodeLocations(signal?: AbortSignal) {
        return this.http.orchestrateRequest<GetFailCodeLocationsResponse>({
            method: 'GET',
            url: '/fail-codes/locations',
            signal,
        });
    }

    /**
     * Fetch fail code frequencies for a given location.
     *
     * @param request - contains the location code
     * @param signal - optional abort signal for cancellation
     * @returns the fail code frequencies
     */
    fetchFailCodeFrequencies(request: GetFailCodeFrequenciesRequest, signal?: AbortSignal) {
        const url = '/fail-codes/frequencies' + this.http.queryString(request);

        return this.http.orchestrateRequest<GetFailCodeFrequenciesResponse>({
            method: 'GET',
            url,
            signal,
        });
    }

    /**
     * Fetch fail code concerns for a given location and frequency.
     *
     * @param request - contains the location code and frequency code
     * @param signal - optional abort signal for cancellation
     * @returns the fail code concerns
     */
    fetchFailCodeConcerns(request: GetFailCodeConcernsRequest, signal?: AbortSignal) {
        const url = '/fail-codes/concerns' + this.http.queryString(request);

        return this.http.orchestrateRequest<GetFailCodeConcernsResponse>({
            method: 'GET',
            url,
            signal,
        });
    }

    // ──────────────────────────────────────────────
    // Diagnostic Trouble Codes (DTCs)
    // ──────────────────────────────────────────────

    /**
     * Search for Diagnostic Trouble Codes (DTCs) by text query.
     *
     * @param request - contains the search text
     * @param signal - optional abort signal for cancellation
     * @returns the DTC codes
     */
    fetchDtcCodes(request: GetDtcCodesRequest, signal?: AbortSignal) {
        const url = '/dtc-codes' + this.http.queryString(request);

        return this.http.orchestrateRequest<GetDtcCodesResponse>({
            method: 'GET',
            url,
            signal,
        });
    }

    // ──────────────────────────────────────────────
    // Diagnostic Trouble Codes (DTCs) - (Techline endpoints)
    // ──────────────────────────────────────────────

    /**
     * Update a DTC code on a Techline case via the NorthStar integration.
     *
     * @param request - contains the DTC code update payload
     * @param signal - optional abort signal for cancellation
     * @returns the patch response
     */
    updateTechlineCaseDtcCode(request: PatchDtcCodeRequest, signal?: AbortSignal) {
        return this.http.orchestrateRequest<PatchDtcCodeResponse>({
            method: 'PATCH',
            url: '/northstar/patchDTCCode/v1',
            body: request.updateBody,
            ignoreCache: true,
            signal,
        });
    }

    // ──────────────────────────────────────────────
    // VIN - (Techline endpoints)
    // ──────────────────────────────────────────────

    /**
     * Validate a VIN via the NorthStar integration.
     *
     * @param request - contains the VIN to validate
     * @param signal - optional abort signal for cancellation
     * @returns the VIN validation response
     */
    validateVin(request: GetVinValidationRequest, signal?: AbortSignal) {
        const url = '/northstar/validateVin/v1' + this.http.queryString(request);

        return this.http.orchestrateRequest<GetVinValidationResponse>({
            method: 'GET',
            url,
            ignoreCache: true,
            signal,
        });
    }

    /**
     * Retrieve vehicle details for a given VIN via the NorthStar integration.
     *
     * @param request - contains the VIN to retrieve details for
     * @param signal - optional abort signal for cancellation
     * @returns the vehicle details response
     */
    fetchVehicleDetails(request: GetVehicleDetailsRequest, signal?: AbortSignal) {
        const url = '/northstar/retrieveVinDetails/v1' + this.http.queryString(request);

        return this.http.orchestrateRequest<GetVehicleDetailsResponse>({
            method: 'GET',
            url,
            ignoreCache: true,
            signal,
        });
    }

    // ──────────────────────────────────────────────
    // CVT
    // ──────────────────────────────────────────────

    /**
     * Create both STS and SBR presigned upload URLs and diagnostic Ids.
     *
     * @param request - contains the VIN and repair order number
     * @returns the presigned upload URLs and diagnostic IDs response
     */
    createCvtUploadUrls(request: PostCvtUploadUrlsRequest) {
        return this.http.orchestrateRequest<PostCvtUploadUrlsResponse>({
            method: 'POST',
            url: `/cvt/upload/v2/${request.vin}/${request.repairOrderNumber}`,
            ignoreCache: true,
        });
    }

    /**
     * Transmit CVT files to SBR using the diagnostic IDs obtained from the createCvtUploadUrls call.
     * This signals SBR to begin processing the uploaded CVT files.
     *
     * @param request - contains the STS and SBR diagnostic IDs along with the upload failure status
     * @returns the CVT send response
     */
    transmitToSbr(request: PostCvtSendRequest) {
        const url = `/cvt/send/v2/${request.stsDiagnosticId}/${request.sbrDiagnosticId}`;
        const body = request.uploadFailureStatus;

        return this.http.orchestrateRequest<PostCvtSendResponse>({
            method: 'POST',
            url,
            body,
            ignoreCache: true,
        });
    }

    /**
     * Upload a CVT (.csv) to S3 from sts-backend.
     * This endpoint is used in the case where the presigned Url failed for sts.
     *
     * @param request - contains the diagnostic ID and the CVT file
     * @returns the file upload response
     */
    uploadCvtFile(request: PostCvtFileUploadRequest) {
        const url = `/cvt/upload/${request.diagnosticId}`;
        return this.http.orchestrateRequest<PostCvtFileUploadResponse>({
            method: 'POST',
            url: url,
            body: request.file,
            ignoreCache: true,
        });
    }

    /**
     * Fetch the CVT Diagnostic details for a given diagnostic ID.
     * This includes the processing status and SBR's response after processing the CVT file.
     * Excludes graph data which is retrieved via a sbrResponseDataUrl provided in the response.
     *
     * @param request - contains the diagnostic ID to retrieve details for
     * @returns the CVT diagnostic details response
     */
    fetchCvtDiagnosticDetails(request: GetCvtDiagnosticDetailsRequest) {
        return this.http.orchestrateRequest<GetCvtDiagnosticDetailsResponse>({
            method: 'GET',
            url: `/cvt/status/v2/${request.diagnosticId}`,
            ignoreCache: true,
        });
    }
}
